/**
 * Authentication utilities
 * Handles sign-out with proper cleanup of OneSignal subscriptions
 */

import { supabase } from '@/integrations/supabase/client';
import { oneSignalService } from './onesignal';
import { isNativeApp } from './platform';

/**
 * Sign out the user with proper cleanup
 * - Unregisters from OneSignal push notifications
 * - Removes external ID link
 * - Clears onesignal_player_id from database
 * - Signs out from Supabase
 */
export async function signOut(): Promise<void> {
  try {
    // Get current user before signing out
    const { data: { user } } = await supabase.auth.getUser();

    if (user && isNativeApp()) {
      console.log('[Auth] Cleaning up OneSignal before sign out...');
      
      try {
        // Remove external ID link from OneSignal
        if (oneSignalService.isAvailable()) {
          await oneSignalService.removeExternalId();
          console.log('[Auth] OneSignal external ID removed');
        }
        
        // Clear the player ID from database
        const { error: dbError } = await supabase
          .from('profiles')
          .update({ 
            onesignal_player_id: null 
          })
          .eq('id', user.id);

        if (dbError) {
          console.error('[Auth] Failed to clear OneSignal player ID from database:', dbError);
        } else {
          console.log('[Auth] OneSignal player ID cleared from database');
        }
      } catch (error) {
        console.error('[Auth] Error during OneSignal cleanup:', error);
        // Continue with sign out even if OneSignal cleanup fails
      }
    }

    // Sign out from Supabase
    await supabase.auth.signOut();
    console.log('[Auth] Sign out complete');
  } catch (error) {
    console.error('[Auth] Error during sign out:', error);
    // Still attempt to sign out even if cleanup fails
    await supabase.auth.signOut();
  }
}
